import { DateRenderer, DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import { _isNil, _notNil } from '@/littledash';
import type { Nullable } from '@/model/Common.model';
import type { FC, ReactNode } from 'react';
import type {
  AuditLogAnimalSummary,
  AuditLogDosageSummary,
  AuditLogEntitySummary,
  AuditLogMeasurementSummary,
  AuditLogObservationSummary,
  AuditLogSampleSummary,
} from '@/components/Studies/AuditLog/AuditLog.model';

/**
 * Shared row primitive for the entity summary card. Handles the null/empty
 * fallback and light typing (date/date-time formatting) so the per-entity
 * display components stay free of formatting concerns.
 */
export const AuditLogEntityPropRow: FC<{
  label: string;
  value: Nullable<ReactNode>;
  type?: 'text' | 'datetime' | 'date';
  valueClassName?: string;
}> = ({ label, value, type = 'text', valueClassName = 'near-black' }) => {
  const rendered = (() => {
    if (_isNil(value) || value === '') {
      return '-';
    }
    if (type === 'datetime' && typeof value === 'string') {
      return <DateTimeRenderer value={value} />;
    }
    if (type === 'date' && typeof value === 'string') {
      return <DateRenderer value={value} />;
    }
    return value;
  })();
  return (
    <div className="flex mb1">
      <span className="dark-gray f6 w-33">{label}</span>
      <span className={`f6 fw5 w-66 ${valueClassName}`}>{rendered}</span>
    </div>
  );
};

const AnimalRows: FC<{ animal: AuditLogAnimalSummary | null | undefined; label?: string }> = ({
  animal,
  label = 'Animal',
}) => {
  if (!_notNil(animal)) {
    return null;
  }
  const base = `${animal.name} (${animal.api_id})`;
  const value = _notNil(animal.deleted_at) ? (
    <>
      {base}{' '}
      <span className="red" data-testid="audit-log-animal-deleted-at">
        — Deleted at <DateTimeRenderer value={animal.deleted_at} />
      </span>
    </>
  ) : (
    base
  );
  return <AuditLogEntityPropRow label={label} value={value} />;
};

export const AnimalDisplay: FC<{ entity: AuditLogAnimalSummary }> = ({ entity }) => (
  <>
    <AuditLogEntityPropRow label="Name" value={entity.name} />
    <AuditLogEntityPropRow label="Catalog ID" value={entity.catalog_id} />
  </>
);

export const SampleDisplay: FC<{ entity: AuditLogSampleSummary }> = ({ entity }) => (
  <>
    <AuditLogEntityPropRow label="Sample ID" value={entity.sample_id} />
    <AuditLogEntityPropRow label="Collected" value={entity.collected_at} type="datetime" />
    <AnimalRows animal={entity.animal} />
  </>
);

export const DosageDisplay: FC<{ entity: AuditLogDosageSummary }> = ({ entity }) => (
  <>
    <AuditLogEntityPropRow label="Dosed" value={entity.dosed_at} type="datetime" />
    <AuditLogEntityPropRow label="Treatment" value={entity.treatment_name} />
    <AnimalRows animal={entity.animal} />
  </>
);

export const ObservationDisplay: FC<{ entity: AuditLogObservationSummary }> = ({ entity }) => (
  <>
    <AuditLogEntityPropRow label="Scoring type" value={entity.observation_type} />
    <AuditLogEntityPropRow label="Observed" value={entity.observed_at} type="datetime" />
    <AnimalRows animal={entity.animal} />
  </>
);

export const MeasurementDisplay: FC<{ entity: AuditLogMeasurementSummary }> = ({ entity }) => (
  <>
    <AuditLogEntityPropRow label="Measured on" value={entity.measured_at} type="date" />
    <AnimalRows animal={entity.animal} />
  </>
);

export const AuditLogEntityDisplay: FC<{ summary: AuditLogEntitySummary }> = ({ summary }) => {
  const { entity_type, entity } = summary;
  if (!_notNil(entity_type) || !_notNil(entity)) {
    return null;
  }
  switch (entity_type) {
    case 'ANIMAL':
      return <AnimalDisplay entity={entity as AuditLogAnimalSummary} />;
    case 'SAMPLE':
      return <SampleDisplay entity={entity as AuditLogSampleSummary} />;
    case 'DOSAGE':
      return <DosageDisplay entity={entity as AuditLogDosageSummary} />;
    case 'OBSERVATION':
      return <ObservationDisplay entity={entity as AuditLogObservationSummary} />;
    case 'MEASUREMENT':
      return <MeasurementDisplay entity={entity as AuditLogMeasurementSummary} />;
    default:
      return null;
  }
};
